import React from "react";
import FormField from "components/v2/FormField";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useAppStore from "store/appStore";
import FlashMessage from "components/v2/FlashMessage";
import useSendEmailChangeOtp from "api/UserResource/useSendEmailChangeOtp";
import useChangeEmail from "api/UserResource/useChangeEmail";

const ChangeEmailAddress = () => {
	const [actionIsSendingOtp, setActionIsSendingOtp] = React.useState(true);
	const [openOtpField, setOpenOtpField] = React.useState(false);

	const msg = useAppStore((state) => state.flashMessage);

	const initialValues = {
		email: "",
		otp: "",
	};

	const validate = Yup.object().shape({
		email: Yup.string().required("Email is required").email("Invalid email address"),
		otp: openOtpField
			? Yup.string().required("One-Time-Passcode is required")
			: Yup.string().notRequired(),
	});

	const { mutateAsync: mutateEmailChangeOtp, isLoading: isSendingOtp } = useSendEmailChangeOtp();

	const { mutateAsync: mutateChangeEmail, isLoading: isChangingEmail } = useChangeEmail();

	const handleChangeEmail = async (data) => {
		if (actionIsSendingOtp) {
			await mutateEmailChangeOtp({
				email: data.email,
			});
			setOpenOtpField(true);
			setActionIsSendingOtp(false);
			return;
		}

		await mutateChangeEmail(data);
		setActionIsSendingOtp(true);
		setOpenOtpField(false);
		return;
	};

	const handleResentOtp = () => {
		setOpenOtpField(false);
		setActionIsSendingOtp(true);
	};

	return (
		<>
			<div className="space-y-6">
				{/* Information Cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
					<div className="p-4 rounded-xl border border-green-200 bg-green-50">
						<div className="flex items-start gap-3">
							<div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
								<svg
									className="w-5 h-5 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							</div>
							<div>
								<h4 className="font-medium text-green-900 mb-1">Email Usage</h4>
								<p className="text-sm text-green-700">
									Your email address is used for login credentials and receiving important
									notifications and updates.
								</p>
							</div>
						</div>
					</div>

					<div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
						<div className="flex items-start gap-3">
							<div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
								<svg
									className="w-5 h-5 text-amber-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									/>
								</svg>
							</div>
							<div>
								<h4 className="font-medium text-amber-900 mb-1">Security Process</h4>
								<p className="text-sm text-amber-700">
									You'll receive an OTP to your current email to confirm the change. This ensures
									account security.
								</p>
							</div>
						</div>
					</div>
				</div>

				<Formik
					initialValues={initialValues}
					validationSchema={validate}
					onSubmit={(data) => handleChangeEmail(data)}
				>
					<Form className="space-y-6">
						{/* Email Input */}
						<div className="space-y-2">
							<FormField
								name="email"
								label="New Email Address *"
								// placeholder="Enter your new email address"
								classNames={{
									label: "text-sm font-medium text-gray-700 mb-1 block",
									input:
										"w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-akgreen focus:border-transparent transition-all duration-200",
								}}
							/>
						</div>

						{/* OTP Section */}
						{openOtpField && (
							<div className="space-y-4 p-6 bg-gray-50 rounded-xl border border-gray-200">
								<div className="flex items-center gap-2 mb-4">
									<div className="w-8 h-8 bg-akgreen rounded-lg flex items-center justify-center">
										<svg
											className="w-5 h-5 text-white"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
									</div>
									<h4 className="font-medium text-gray-900">Verify Your Email</h4>
								</div>

								{msg && <FlashMessage />}

								<div className="space-y-2">
									<FormField
										name="otp"
										label="One-Time Password (OTP) *"
										// placeholder="Enter the OTP sent to your email"
										classNames={{
											label: "text-sm font-medium text-gray-700 mb-1 block",
											input:
												"w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-akgreen focus:border-transparent transition-all duration-200",
										}}
									/>
								</div>

								<button
									type="button"
									className="text-akgreen hover:text-akgreener font-medium text-sm transition-colors duration-200"
									onClick={() => handleResentOtp()}
								>
									OTP not received? Click here to resend
								</button>
							</div>
						)}

						{/* Submit Button */}
						<div className="pt-4">
							<button
								type="submit"
								disabled={isSendingOtp || isChangingEmail}
								className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-akgreen to-akgreener text-white font-medium rounded-xl hover:from-akgreener hover:to-akgreen focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 min-w-[160px]"
							>
								{isSendingOtp || isChangingEmail ? (
									<>
										<AiOutlineLoading3Quarters size={20} className="animate-spin" />
										<span>{openOtpField ? "Updating..." : "Sending..."}</span>
									</>
								) : (
									<span>{openOtpField ? "Update Email" : "Send OTP"}</span>
								)}
							</button>
						</div>
					</Form>
				</Formik>
			</div>
		</>
	);
};

export default ChangeEmailAddress;
